import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Load user on mount - check token OR localStorage user (from Google OAuth)
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (token) {
      loadUser();
    } else if (storedUser) {
      // Restore Google OAuth user from localStorage
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Failed to parse stored user:', e);
      }
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [token]);

  const loadUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to load user:', error);
      // Token might be expired
      logout();
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      toast.success('Account created successfully! Please login.');
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });
      const { access_token } = response.data;
      
      // Save token
      localStorage.setItem('token', access_token);
      setToken(access_token);
      
      // Load user data
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      setUser(userResponse.data);
      
      toast.success('Welcome back!');
      return { success: true, token: access_token };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user'); // Also remove stored Google OAuth user
    setToken(null);
    setUser(null);
    toast.info('Logged out successfully');
  };

  const isAuthenticated = () => {
    // Check for either JWT token OR user object (from Google OAuth cookie session)
    return !!user;
  };

  const setUserData = (userData) => {
    // Helper to set user data (used by Google OAuth callback)
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const value = {
    user,
    token,
    loading,
    register,
    login,
    logout,
    isAuthenticated,
    loadUser,
    setUser: setUserData // Expose setUser for Google OAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
